import { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import { sqlRead, turnoverWrite as sqlWrite, clean } from "../utils/sqlClient";
import {
  MAX_FEEDBACK_ATTACHMENTS, ACCEPTED_IMAGE_TYPES, selectAttachmentFiles, fileToAttachment,
  attachmentsPayload, imageFilesFromClipboard,
} from "../utils/feedbackAttachments"; // screenshots on a submission (FeedbackAttachments, db/06)
import { callClaude } from "../utils/claudeClient";
import { copyFeedbackToGitHub } from "../utils/githubIssueClient";
import {
  COMPLEXITY_LEVELS, COMPLEXITY_BY_KEY, buildTriageUserContent, parseTriageResponse, parseStoredTriage,
  triageSavePayload, matchesComplexityFilter, complexityCounts, implementationBrief,
} from "../utils/feedbackTriage"; // AI triage (FeedbackItems.AITriage*, db/06; prompt category feedback_triage, db/05)
import { feedbackNumberLabel, matchesFeedbackQuery } from "../utils/feedbackNumber";
import { useNavHeight } from "../hooks/useNavHeight";
import { fmtDate } from "./turnover/shared";

import { useIsAdmin } from "../auth/AdminContext"; // Feedback Inbox is admin-only (SWA-ESTIMATING-ADMIN)

// ── helpers ───────────────────────────────────────────────────────────────────
const cleanStr = (s) => clean(s).trim();

// AI triage of one submission: fetch the 'feedback_triage' system prompt →
// one Claude call through the proxy (text + screenshot image blocks) → parse
// → sp_SaveFeedbackTriage. Advisory only — classifies and proposes, never
// implements. Called fire-and-forget after a successful submit (the
// submission is already stored; a triage failure is logged, not shown as a
// submit error) and from the Inbox's "Re-run AI triage". Returns the triage
// object, or null when it could not run.
const TRIAGE_MODEL_LABEL = "claude-proxy default";
async function triageFeedbackItem({ feedbackId, title, description, type, priority, submittedBy, attachments = [] }) {
  const id = Number(feedbackId);
  if (!id) return null;
  const promptRows = await sqlRead("EXEC sp_GetSystemPrompt @category='feedback_triage'");
  const systemPrompt = promptRows?.[0]?.FullPrompt;
  if (!systemPrompt) throw new Error("No 'feedback_triage' system prompt configured (Setup ▸ AI System Prompts)");
  const content = buildTriageUserContent({ title, description, type, priority, submittedBy, attachments });
  const text = await callClaude(systemPrompt, content, 1500);
  const triage = parseTriageResponse(text);
  await sqlWrite("EXEC sp_SaveFeedbackTriage @payload = @payload", { payload: JSON.stringify(triageSavePayload(id, triage, TRIAGE_MODEL_LABEL)) });
  return triage;
}

// ── STATUS badge ──────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  "New":         { bg: "#dbeafe", color: "#1e40af" },
  "In Progress": { bg: "#fef3c7", color: "#92400e" },
  "Resolved":    { bg: "#d1fae5", color: "#065f46" },
  "Won't Fix":   { bg: "#fee2e2", color: "#991b1b" },
};
const TYPE_STYLES = {
  "Bug":        { bg: "#fee2e2", color: "#991b1b" },
  "Suggestion": { bg: "#ede9fe", color: "#5b21b6" },
};
function Badge({ text, styles }) {
  const s = styles?.[text] || { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 100, ...s }}>
      {text}
    </span>
  );
}

// ── FEEDBACK FORM ─────────────────────────────────────────────────────────────
function FeedbackForm({ user, onSubmitted }) {
  const [type,        setType]        = useState("Bug");
  const [title,       setTitle]       = useState("");
  const [description, setDescription] = useState("");
  const [priority,    setPriority]    = useState("Medium");
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState(null);
  // Screenshots: [{ id, fileName, contentType, dataUrl, sizeBytes }], downscaled
  // in the browser (fileToAttachment) and sent as a bound @attachments param.
  const [attachments, setAttachments] = useState([]);
  const [attachNote,  setAttachNote]  = useState(null); // rejection reasons / read errors, shown under the strip
  const [attaching,   setAttaching]   = useState(false);
  const [dragOver,    setDragOver]    = useState(false);
  const fileInputRef = useRef(null);

  async function addFiles(fileList) {
    const { accepted, rejected } = selectAttachmentFiles(fileList, attachments.length);
    const notes = rejected.map(r => `${r.name}: ${r.reason}`);
    if (accepted.length) {
      setAttaching(true);
      try {
        for (const f of accepted) {
          try {
            const a = await fileToAttachment(f);
            setAttachments(prev => prev.length >= MAX_FEEDBACK_ATTACHMENTS ? prev : [...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ...a }]);
          } catch (e) {
            notes.push(e.message);
          }
        }
      } finally { setAttaching(false); }
    }
    setAttachNote(notes.length ? notes.join(" ") : null);
  }
  function removeAttachment(id) { setAttachments(prev => prev.filter(a => a.id !== id)); }
  function handlePaste(e) {
    const files = imageFilesFromClipboard(e.clipboardData);
    if (!files.length) return; // plain text paste into the fields proceeds normally
    e.preventDefault();
    addFiles(files);
  }
  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer?.files);
  }

  async function handleSubmit() {
    if (!title.trim() || !description.trim()) {
      setError("Please fill in both a title and description.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Both values are BOUND parameters (never inlined in the EXEC text):
      // the attachments are multi-hundred-KB data URLs, and binding the
      // payload too means an apostrophe in "doesn't work" survives instead of
      // being stripped by clean().
      const payload = {
        Type:             cleanStr(type),
        Title:            title.trim(),
        Description:      description.trim(),
        Priority:         cleanStr(priority),
        SubmittedByName:  (user?.name || "").trim(),
        SubmittedByEmail: (user?.username || "").trim(),
      };
      const params = { payload: JSON.stringify(payload) };
      let query = "EXEC sp_SubmitFeedback @payload = @payload";
      const attachmentRows = attachmentsPayload(attachments);
      if (attachmentRows.length) {
        params.attachments = JSON.stringify(attachmentRows);
        query += ", @attachments = @attachments";
      }
      const res = await sqlWrite(query, params);
      const feedbackId = res?.ResultSets?.Table1?.[0]?.FeedbackID ?? res?.Table1?.[0]?.FeedbackID ?? null;
      // Fire-and-forget AI triage. The submission is already stored; if triage
      // fails the admin sees "Untriaged" in the inbox and can re-run it.
      if (feedbackId) {
        copyFeedbackToGitHub(feedbackId); // fire-and-forget; never throws, never blocks the submit
        triageFeedbackItem({
          feedbackId, title: payload.Title, description: payload.Description, type: payload.Type, priority: payload.Priority,
          submittedBy: payload.SubmittedByName, attachments,
        }).catch(err => console.warn("[Help] feedback triage skipped:", err?.message || err));
      }
      setType("Bug");
      setTitle("");
      setDescription("");
      setPriority("Medium");
      setAttachments([]);
      setAttachNote(null);
      onSubmitted?.();
    } catch (e) {
      setError(`Submission failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      background: "#fff",
      border: "1px solid var(--cs-border)",
      borderRadius: 10,
      padding: 28,
      maxWidth: 620,
    }} onPaste={handlePaste}>
      <div style={{ marginBottom: 22 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>
          Report an Issue or Suggest an Improvement
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: "var(--text3)" }}>
          Tell us about a bug you encountered or an idea to make EstAssist better.
        </p>
      </div>

      {/* Type + Priority row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Type</label>
          <div style={{ display: "flex", gap: 8 }}>
            {["Bug", "Suggestion"].map(t => (
              <button
                key={t}
                onClick={() => setType(t)}
                style={{
                  flex: 1,
                  padding: "7px 0",
                  borderRadius: 6,
                  border: type === t ? "2px solid var(--cs-blue)" : "2px solid var(--cs-border)",
                  background: type === t ? "#EBF4FF" : "#fff",
                  color: type === t ? "var(--cs-blue)" : "var(--text2)",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {t === "Bug" ? "🐛" : "💡"} {t}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Priority</label>
          <select
            value={priority}
            onChange={e => setPriority(e.target.value)}
            style={{ width: "100%" }}
          >
            <option>Low</option>
            <option>Medium</option>
            <option>High</option>
          </select>
        </div>
      </div>

      {/* Title */}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Title <span style={{ color: "var(--red)" }}>*</span></label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={type === "Bug" ? "e.g., Quote drawer closes when I scroll" : "e.g., Add PDF export to estimates"}
          style={{ width: "100%", boxSizing: "border-box" }}
          maxLength={200}
        />
      </div>

      {/* Description */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          Description <span style={{ color: "var(--red)" }}>*</span>
        </label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder={
            type === "Bug"
              ? "Steps to reproduce, what you expected, what actually happened…"
              : "Describe the idea, the problem it solves, and how you expect it to work…"
          }
          rows={5}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical", minHeight: 100 }}
          maxLength={2000}
        />
        <div style={{ fontSize: 11, color: "var(--text3)", textAlign: "right", marginTop: 3 }}>
          {description.length}/2000
        </div>
      </div>

      {/* Screenshots */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          Screenshots <span style={{ fontWeight: 400, color: "var(--text3)" }}>(optional, up to {MAX_FEEDBACK_ATTACHMENTS})</span>
        </label>
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            border: `1.5px dashed ${dragOver ? "var(--cs-blue)" : "var(--cs-border)"}`,
            background: dragOver ? "#EBF4FF" : "#FAFBFC",
            borderRadius: 8, padding: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
            transition: "all 0.15s",
          }}
        >
          {attachments.map((a, i) => (
            <div key={a.id} style={{ position: "relative", width: 96, height: 72, borderRadius: 6, overflow: "hidden", border: "1px solid var(--cs-border)", background: "#fff" }} title={`${a.fileName}${a.width ? ` · ${a.width}×${a.height}` : ""} · ${Math.round(a.sizeBytes / 1024)} KB`}>
              <img src={a.dataUrl} alt={a.fileName || `Screenshot ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                aria-label={`Remove ${a.fileName || "screenshot"}`}
                style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: 100, border: "none", background: "rgba(17,24,39,0.75)", color: "#fff", fontSize: 12, lineHeight: "20px", padding: 0, cursor: "pointer" }}
              >×</button>
            </div>
          ))}
          {attachments.length < MAX_FEEDBACK_ATTACHMENTS && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={attaching}
              style={{ width: 96, height: 72, borderRadius: 6, border: "1px dashed var(--cs-border)", background: "#fff", color: "var(--text2)", fontSize: 12, fontWeight: 600, cursor: attaching ? "wait" : "pointer" }}
            >
              {attaching ? "Adding…" : "＋ Add image"}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(",")}
            multiple
            hidden
            onChange={e => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <span style={{ fontSize: 11.5, color: "var(--text3)", flexBasis: "100%" }}>
            Click to choose, drag images here, or paste a screenshot (Win+Shift+S, then Ctrl+V anywhere on this form). Images are resized to 1600 px before upload.
          </span>
        </div>
        {attachNote && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#92400E" }}>{attachNote}</div>
        )}
      </div>

      {error && (
        <div style={{ marginBottom: 14, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, fontSize: 13, color: "#991B1B" }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            background: "var(--cs-blue)",
            color: "#fff",
            border: "none",
            borderRadius: 7,
            padding: "9px 22px",
            fontSize: 13,
            fontWeight: 600,
            cursor: submitting ? "not-allowed" : "pointer",
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
        <span style={{ fontSize: 12, color: "var(--text3)" }}>
          Submitted as {user?.name || user?.username}
        </span>
      </div>
    </div>
  );
}

// ── FEEDBACK INBOX (admin) ────────────────────────────────────────────────────
function FeedbackInbox() {
  const { accounts } = useMsal();
  const me = accounts[0];
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [filter,  setFilter]  = useState("All");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const rows = await sqlRead("EXEC sp_GetFeedback");
      setItems(rows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Closing (Resolved / Won't Fix) carries a resolution note and the name of who closed it — the signed-in admin.
  async function updateStatus(feedbackId, status, resolution = null) {
    try {
      const body = { FeedbackID: feedbackId, Status: cleanStr(status) };
      const by = { name: (me?.name || "").trim(), email: (me?.username || "").trim() };
      if (resolution) { body.ResolutionNote = cleanStr(resolution.note); body.ResolvedByName = cleanStr(by.name || by.email); body.ResolvedByEmail = cleanStr(by.email); }
      await sqlWrite(`EXEC sp_UpdateFeedbackStatus @payload = '${cleanStr(JSON.stringify(body))}'`);
      const now = new Date().toISOString();
      setItems(prev => prev.map(i => i.FeedbackID !== feedbackId ? i : {
        ...i, Status: status,
        ...(resolution ? { ResolutionNote: resolution.note.trim(), ResolvedByName: by.name || by.email, ResolvedByEmail: by.email, ...(status === "Resolved" ? { ResolvedAt: now } : {}) } : {}),
      }));
      return true;
    } catch (e) {
      alert(`Failed to update: ${e.message}`);
      return false;
    }
  }

  // Re-run (or first-run) AI triage for one card; patches the row in place so
  // the card re-renders without a full reload.
  async function retriage(item, attachments) {
    const triage = await triageFeedbackItem({
      feedbackId: item.FeedbackID, title: item.Title, description: item.Description, type: item.Type, priority: item.Priority,
      submittedBy: item.SubmittedByName, attachments,
    });
    if (triage) {
      setItems(prev => prev.map(i => i.FeedbackID === item.FeedbackID
        ? { ...i, AITriageJson: JSON.stringify(triage), AIComplexity: triage.complexity, AIArea: triage.area, AITriagedAt: new Date().toISOString() }
        : i));
    }
    return triage;
  }

  const filters = ["All", "New", "In Progress", "Resolved", "Won't Fix"];
  const [complexityFilter, setComplexityFilter] = useState("all"); // 'all' | level key | 'untriaged' | 'auto'
  const [search, setSearch] = useState(""); // "29" / "#29" finds that item; words search title, description, submitter
  const byStatus = (filter === "All" ? items : items.filter(i => i.Status === filter)).filter(i => matchesFeedbackQuery(i, search));
  const filtered = byStatus.filter(i => matchesComplexityFilter(i, complexityFilter));
  const counts = complexityCounts(byStatus);
  const complexityChips = [
    { key: "all", label: "Any size" },
    { key: "auto", label: "⚡ Quick-win candidates", title: "Trivial / small, ≥ 70% confidence, no open questions — a bounded hand-off for a developer to build and open a PR for review" },
    ...COMPLEXITY_LEVELS.map(l => ({ key: l.key, label: l.label, title: l.hint })),
    { key: "untriaged", label: "Untriaged" },
  ];

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>
          Feedback Inbox
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: "var(--text3)" }}>
          Issues and suggestions submitted by the team.
        </p>
      </div>

      {/* Search - by feedback number (#29) or words */}
      <input
        type="search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Find by number (#29) or words…"
        aria-label="Search feedback by number or words"
        style={{ width: "100%", maxWidth: 320, boxSizing: "border-box", padding: "6px 10px", marginBottom: 12, fontSize: 13, border: "1.5px solid var(--cs-border)", borderRadius: 8, background: "#fff", color: "var(--text)" }}
      />

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "4px 12px",
              borderRadius: 100,
              border: filter === f ? "1.5px solid var(--cs-blue)" : "1.5px solid var(--cs-border)",
              background: filter === f ? "#EBF4FF" : "#fff",
              color: filter === f ? "var(--cs-blue)" : "var(--text2)",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {f}
            {f !== "All" && items.filter(i => i.Status === f).length > 0 && (
              <span style={{ marginLeft: 5, opacity: 0.7 }}>
                ({items.filter(i => i.Status === f).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* AI triage size chips */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginRight: 2 }}>AI triage:</span>
        {complexityChips.map(c => {
          const n = counts[c.key] ?? 0;
          const lvl = COMPLEXITY_BY_KEY[c.key];
          const active = complexityFilter === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setComplexityFilter(c.key)}
              title={c.title || ""}
              style={{
                padding: "3px 10px", borderRadius: 100, fontSize: 11.5, fontWeight: 500, cursor: "pointer",
                border: active ? "1.5px solid var(--cs-blue)" : "1.5px solid var(--cs-border)",
                background: active ? "#EBF4FF" : lvl ? lvl.bg : "#fff",
                color: active ? "var(--cs-blue)" : lvl ? lvl.color : "var(--text2)",
                opacity: n === 0 && c.key !== "all" ? 0.55 : 1,
              }}
            >
              {c.label}{c.key !== "all" && <span style={{ marginLeft: 5, opacity: 0.7 }}>({n})</span>}
            </button>
          );
        })}
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          <div className="spinner spinner-dark" style={{ display: "inline-block", marginRight: 8 }} />
          Loading…
        </div>
      )}
      {error && (
        <div style={{ padding: 20, color: "var(--red)", fontSize: 13 }}>{error}</div>
      )}
      {!loading && !error && filtered.length === 0 && (
        <div style={{ padding: 48, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          No {filter !== "All" ? filter.toLowerCase() : ""} items
        </div>
      )}

      {!loading && !error && filtered.map(item => (
        <FeedbackCard key={item.FeedbackID} item={item} onStatusChange={updateStatus} onRetriage={retriage} resolverName={me?.name || me?.username || ""} />
      ))}
    </div>
  );
}

function FeedbackCard({ item, onStatusChange, onRetriage, resolverName = "" }) {
  const [expanded, setExpanded] = useState(false);
  const [closing,  setClosing]  = useState(null);   // "Resolved" | "Won't Fix" while the resolution note is being written
  const [closeNote, setCloseNote] = useState("");
  const [closeBusy, setCloseBusy] = useState(false);
  async function confirmClose() {
    setCloseBusy(true);
    const ok = await onStatusChange(item.FeedbackID, closing, { note: closeNote });
    setCloseBusy(false);
    if (ok) { setClosing(null); setCloseNote(""); }
  }
  const [hover,    setHover]    = useState(false);
  // AI triage (advisory): stored on the row; re-runnable; copy-as-brief.
  const triage = parseStoredTriage(item);
  const triageLevel = triage ? (COMPLEXITY_BY_KEY[triage.complexity] || COMPLEXITY_BY_KEY.unclear) : null;
  const [triaging,  setTriaging]  = useState(false);
  const [triageErr, setTriageErr] = useState(null);
  const [copied,    setCopied]    = useState(false);
  async function handleRetriage(e) {
    e.stopPropagation();
    setTriaging(true); setTriageErr(null);
    try { await onRetriage?.(item, shots || []); }
    catch (err) { setTriageErr(err?.message || String(err)); }
    finally { setTriaging(false); }
  }
  async function copyBrief(e) {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(implementationBrief(item, triage));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { alert(`Could not copy: ${err?.message || err}`); }
  }
  // Screenshots load only once the card is opened (sp_GetFeedback carries just
  // AttachmentCount so the inbox list never pulls every image blob).
  const [shots,     setShots]     = useState(null); // null = not loaded yet
  const [shotsErr,  setShotsErr]  = useState(null);
  const [lightbox,  setLightbox]  = useState(null); // an attachment row being viewed full-size
  const attachmentCount = Number(item.AttachmentCount) || 0;
  useEffect(() => {
    if (!expanded || shots !== null || attachmentCount === 0) return;
    let alive = true;
    sqlRead(`EXEC sp_GetFeedbackAttachments @feedback_id = ${Number(item.FeedbackID) || 0}`)
      .then(rows => { if (alive) setShots(rows || []); })
      .catch(e => { if (alive) { setShots([]); setShotsErr(e.message); } });
    return () => { alive = false; };
  }, [expanded, shots, attachmentCount, item.FeedbackID]);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = e => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid var(--cs-border)",
        borderRadius: 8,
        marginBottom: 10,
        overflow: "hidden",
        boxShadow: hover ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 12 }}
        onClick={() => setExpanded(e => !e)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)", fontVariantNumeric: "tabular-nums" }} title="Feedback number - quote it when you talk about this item">{feedbackNumberLabel(item)}</span>
            <Badge text={item.Type} styles={TYPE_STYLES} />
            <Badge text={item.Status} styles={STATUS_STYLES} />
            {Number(item.GitHubIssueNumber) > 0 && /^https:\/\/github\.com\//.test(item.GitHubIssueUrl || "") && (
              <a href={item.GitHubIssueUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                 style={{ fontSize: 11, fontWeight: 600, color: "var(--cs-blue)" }} title="Open the GitHub issue">
                GitHub #{item.GitHubIssueNumber}
              </a>
            )}
            <span style={{
              fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 100,
              background: item.Priority === "High" ? "#FEE2E2" : item.Priority === "Medium" ? "#FEF3C7" : "#F3F4F6",
              color:      item.Priority === "High" ? "#991B1B" : item.Priority === "Medium" ? "#92400E" : "#374151",
            }}>
              {item.Priority}
            </span>
            <span style={{ fontSize: 11, color: "var(--text3)" }}>
              {item.SubmittedByName} · {fmtDate(item.SubmittedAt)}
            </span>
            {triageLevel && (
              <span
                style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 100, background: triageLevel.bg, color: triageLevel.color }}
                title={`AI triage: ${triageLevel.label} · ${Math.round(triage.confidence * 100)}% confidence${triage.area ? ` · ${triage.area}` : ""}`}
              >
                {triage.autoCandidate ? "⚡ " : ""}{triageLevel.label}
              </span>
            )}
            {attachmentCount > 0 && (
              <span style={{ fontSize: 11, color: "var(--text3)" }} title={`${attachmentCount} screenshot${attachmentCount === 1 ? "" : "s"} attached`}>
                📎 {attachmentCount}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.Title}
          </div>
        </div>
        <span style={{ color: "var(--text3)", fontSize: 12, flexShrink: 0, marginTop: 2 }}>
          {expanded ? "▲" : "▼"}
        </span>
      </div>

      {expanded && (
        <div style={{ padding: "0 16px 14px", borderTop: "1px solid var(--cs-border)" }}>
          <p style={{ fontSize: 13, color: "var(--text2)", margin: "12px 0 14px", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {item.Description}
          </p>
          {/* AI triage — advisory. Classifies + proposes; a human decides. */}
          <div style={{ marginBottom: 14, padding: "10px 12px", border: "1px solid var(--cs-border)", borderRadius: 8, background: "#FAFBFC" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: triage ? 8 : 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>AI triage</span>
              {triage ? (
                <>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 100, background: triageLevel.bg, color: triageLevel.color }} title={triageLevel.hint}>{triageLevel.label}</span>
                  <span style={{ fontSize: 11, color: "var(--text3)" }}>{Math.round(triage.confidence * 100)}% confidence</span>
                  {triage.area && <span style={{ fontSize: 11, color: "var(--text2)" }}>· {triage.area}</span>}
                  <span style={{ fontSize: 11, color: "var(--text3)" }}>· {triage.category.replace("_", " ")}</span>
                  {triage.autoCandidate && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 100, background: "#FEF9C3", color: "#854D0E" }} title="Trivial / small, ≥ 70% confidence, no open questions. A bounded hand-off: copy the brief, have a developer (or a coding agent run by one) build it as a PR for review. Nothing is implemented automatically.">
                      ⚡ Quick-win candidate
                    </span>
                  )}
                  {item.AITriagedAt && <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: "auto" }}>{fmtDate(item.AITriagedAt)}</span>}
                </>
              ) : (
                <span style={{ fontSize: 12, color: "var(--text3)" }}>Not triaged yet.</span>
              )}
            </div>
            {triage && (
              <div style={{ fontSize: 12.5, color: "var(--text2)", lineHeight: 1.55 }}>
                {triage.summary && <div style={{ marginBottom: 6 }}>{triage.summary}</div>}
                {triage.proposedChange && (
                  <div style={{ marginBottom: 6 }}><strong style={{ color: "var(--text)" }}>Proposed change:</strong> {triage.proposedChange}</div>
                )}
                {triage.likelyFiles.length > 0 && (
                  <div style={{ marginBottom: 6, fontSize: 11.5 }}><strong>Likely files:</strong> {triage.likelyFiles.map(f => <code key={f} style={{ marginRight: 6 }}>{f}</code>)}</div>
                )}
                {triage.questions.length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <strong style={{ color: "var(--text)" }}>Open questions:</strong>
                    <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{triage.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                  </div>
                )}
                {triage.riskNotes && <div style={{ marginBottom: 6, color: "#92400E" }}><strong>Risk:</strong> {triage.riskNotes}</div>}
                {triage.duplicateHint && <div style={{ fontSize: 11.5, color: "var(--text3)" }}>Possible duplicate: {triage.duplicateHint}</div>}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" onClick={handleRetriage} disabled={triaging} style={{ fontSize: 11.5, padding: "3px 10px" }}>
                {triaging ? "Triaging…" : triage ? "↻ Re-run AI triage" : "▶ Run AI triage"}
              </button>
              {triage && (
                <button type="button" onClick={copyBrief} style={{ fontSize: 11.5, padding: "3px 10px" }} title="Copies a markdown brief (submission + triage + hand-off instructions) for a developer or a coding agent run by one. The result is a PR for review — never a merge.">
                  {copied ? "✓ Copied" : "📋 Copy implementation brief"}
                </button>
              )}
              {triageErr && <span style={{ fontSize: 11.5, color: "var(--red)" }}>{triageErr}</span>}
            </div>
          </div>
          {attachmentCount > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 6 }}>Screenshots</div>
              {shots === null && !shotsErr && <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading…</div>}
              {shotsErr && <div style={{ fontSize: 12, color: "var(--red)" }}>Could not load screenshots: {shotsErr}</div>}
              {shots && shots.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {shots.map((s, i) => (
                    <button
                      key={s.AttachmentID}
                      type="button"
                      onClick={e => { e.stopPropagation(); setLightbox(s); }}
                      title={`${s.FileName || `Screenshot ${i + 1}`} — click to enlarge`}
                      style={{ width: 140, height: 100, padding: 0, borderRadius: 6, overflow: "hidden", border: "1px solid var(--cs-border)", background: "#fff", cursor: "zoom-in" }}
                    >
                      <img src={s.DataUrl} alt={s.FileName || `Screenshot ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {lightbox && (
            <div
              onClick={() => setLightbox(null)}
              style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.85)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, cursor: "zoom-out" }}
            >
              <img src={lightbox.DataUrl} alt={lightbox.FileName || "Screenshot"} style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 6, boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }} />
              <div style={{ position: "absolute", bottom: 16, left: 0, right: 0, textAlign: "center", color: "#E5E7EB", fontSize: 12 }}>
                {lightbox.FileName || "Screenshot"} · click or Esc to close
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, color: "var(--text3)", fontWeight: 600 }}>Status:</label>
            <select
              value={item.Status}
              onChange={e => {
                const v = e.target.value;
                if ((v === "Resolved" || v === "Won't Fix") && v !== item.Status) setClosing(v);   // closing asks for a note first
                else { setClosing(null); onStatusChange(item.FeedbackID, v); }
              }}
              style={{ fontSize: 12, padding: "4px 8px" }}
              onClick={e => e.stopPropagation()}
            >
              <option>New</option>
              <option>In Progress</option>
              <option>Resolved</option>
              <option value="Won't Fix">Won&apos;t Fix</option>
            </select>
          </div>
          {closing && (
            <div onClick={e => e.stopPropagation()} style={{ marginTop: 10, padding: 10, border: "1px solid var(--cs-border)", borderRadius: 6, background: "#F8FAFC" }} data-testid="close-panel">
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                {closing === "Resolved" ? "Resolve this item" : "Close as Won't Fix"} — <span style={{ fontWeight: 500, color: "var(--text3)" }}>by {resolverName || "you"}</span>
              </div>
              <textarea
                value={closeNote} onChange={e => setCloseNote(e.target.value)} maxLength={2000} rows={3}
                placeholder={closing === "Resolved" ? "What was done? (e.g. “Fixed in PR #430 — field no longer loses focus”)" : "Why won't this be fixed?"}
                aria-label="Resolution note"
                style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: 6, border: "1px solid var(--cs-border)", borderRadius: 4, fontFamily: "inherit" }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <button type="button" disabled={closeBusy || !closeNote.trim()} onClick={confirmClose}
                  style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 6, border: "none", background: "var(--cs-blue)", color: "#fff", cursor: closeBusy || !closeNote.trim() ? "not-allowed" : "pointer", opacity: closeBusy || !closeNote.trim() ? 0.5 : 1 }}>
                  {closeBusy ? "Saving…" : closing === "Resolved" ? "Mark resolved" : "Close as Won't Fix"}
                </button>
                <button type="button" disabled={closeBusy} onClick={() => { setClosing(null); setCloseNote(""); }}
                  style={{ fontSize: 12, padding: "4px 12px", borderRadius: 6, border: "1px solid var(--cs-border)", background: "#fff", cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}
          {(item.Status === "Resolved" || item.Status === "Won't Fix") && (item.ResolutionNote || item.ResolvedByName) && (
            <div style={{ marginTop: 10, padding: 10, borderLeft: "3px solid #86efac", background: "#F0FDF4", borderRadius: 4, fontSize: 12.5 }} data-testid="resolution-record">
              <div style={{ fontWeight: 700, marginBottom: 2 }}>
                {item.Status === "Resolved" ? "Resolved" : "Closed as Won't Fix"}{item.ResolvedByName ? ` by ${item.ResolvedByName}` : ""}{item.ResolvedAt && item.Status === "Resolved" ? ` · ${fmtDate(item.ResolvedAt)}` : ""}
              </div>
              {item.ResolutionNote && <div style={{ whiteSpace: "pre-wrap" }}>{item.ResolutionNote}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function Help() {
  const { accounts } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const user = accounts[0];
  const isAdmin = useIsAdmin() === true;
  const [submitted, setSubmitted] = useState(false);

  const isInbox = location.pathname === "/help/inbox";
  const navHeight = useNavHeight();

  function handleSubmitted() {
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 6000);
  }

  return (
    <div style={{ display: "flex", height: `calc(100vh - ${navHeight}px)` }}>

      {/* Sidebar */}
      <div style={{
        width: 200,
        flexShrink: 0,
        borderRight: "1px solid var(--cs-border)",
        background: "#FAFBFC",
        padding: "20px 0",
      }}>
        <div style={{ padding: "0 16px 8px", fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
          Help
        </div>
        <SidebarLink
          path="/help/feedback"
          label="Report an Issue"
          icon="🐛"
          currentPath={location.pathname}
          onClick={() => navigate("/help/feedback")}
        />

        {isAdmin && (
          <>
            <div style={{ padding: "14px 16px 6px", fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Admin
            </div>
            <SidebarLink
              path="/help/inbox"
              label="Feedback Inbox"
              icon="📋"
              currentPath={location.pathname}
              onClick={() => navigate("/help/inbox")}
            />
            <SidebarLink
              path="/integrations"
              label="Integrations"
              icon="🔌"
              currentPath={location.pathname}
              onClick={() => navigate("/integrations")}
            />
          </>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 32 }}>
        {submitted && (
          <div style={{
            marginBottom: 20, padding: "12px 18px",
            background: "#D1FAE5", border: "1px solid #6EE7B7",
            borderRadius: 8, fontSize: 13, color: "#065F46",
            display: "flex", alignItems: "center", gap: 8, maxWidth: 620,
          }}>
            <span>✓</span>
            <span><strong>Submitted!</strong> Thank you — Nick will review your feedback shortly.</span>
          </div>
        )}

        {isInbox && isAdmin
          ? <FeedbackInbox />
          : <FeedbackForm user={user} onSubmitted={handleSubmitted} />
        }
      </div>
    </div>
  );
}

function SidebarLink({ path, label, icon, currentPath, onClick }) {
  const active = currentPath === path;
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        width: "100%", padding: "7px 16px",
        background: active ? "#EBF4FF" : "transparent",
        border: "none", cursor: "pointer",
        fontSize: 12, fontWeight: active ? 600 : 400,
        color: active ? "var(--cs-blue)" : "var(--text2)",
        textAlign: "left",
        borderLeft: active ? "2px solid var(--cs-blue)" : "2px solid transparent",
        transition: "all 0.1s",
      }}
    >
      <span>{icon}</span> {label}
    </button>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--text2)",
  marginBottom: 5,
};
